/**
 * ResizablePanel — the ONE side-panel chrome: resize + collapse, composed
 * (DESIGN-SYSTEM.md §3). Wrap any panel (left nav, right details inspector) to
 * give it a drag separator AND a uniform collapse system, without the panel
 * knowing anything about either. The panel fills the width this component owns.
 *
 *   <ResizablePanel side="left"  collapseKey="…"> <WorkspaceNav … /> </ResizablePanel>
 *   <ResizablePanel side="right" collapseKey="…"> <Sidebar … />      </ResizablePanel>
 *
 * Collapse, two ways, both handled here:
 *   - DRAG the separator past the panel's minimum, toward its outer edge, and it
 *     scales "into nothing" → collapsed.
 *   - PRESS the collapse button: a small tab on the panel's INNER edge, at the
 *     top, in line with the panel header.
 * When collapsed, the panel disappears and the expand button stays.
 *
 * Collapse state is controlled (`collapsed` + `onCollapsedChange`) when a host
 * needs to read it, else internal + persisted under `collapseKey`. Width
 * persists under `widthKey`. Pick min/max/fraction from WIDTH_TIERS.
 *
 * Renders as fragment siblings ordered by `side`, so it drops into a flex row.
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cx } from "./classNames.js";

export interface ResizablePanelProps {
  readonly children: ReactNode;
  /** Which side the panel sits on; the separator + collapse tab hug the inner edge. */
  readonly side?: "left" | "right";
  /** Persist the resized width under this localStorage key. */
  readonly widthKey?: string;
  readonly defaultWidth?: number;
  readonly minWidth?: number;
  readonly maxWidth?: number;
  /** Cap the RENDERED width at this fraction of the viewport so the panel can't
   *  push its siblings off-screen on a narrow viewport (the stored width is
   *  kept — it just renders clamped, and grows back when the window widens).
   *  Computed in JS so a drag starts from what's actually shown (no dead slack).
   *  Default 0.42 (a side rail); pass higher for a primary content panel. */
  readonly maxViewportFraction?: number;
  /** Controlled collapse (omit for internal state). */
  readonly collapsed?: boolean;
  readonly onCollapsedChange?: (collapsed: boolean) => void;
  /** Persist internal collapse under this key (ignored when controlled). */
  readonly collapseKey?: string;
  /** Initial internal collapse when nothing is persisted yet (default false). */
  readonly defaultCollapsed?: boolean;
  /** Allow collapsing at all (drag-to-collapse + the handle). Default true. */
  readonly collapsible?: boolean;
  /** Render the built-in chevron handle on the separator. Default true. Set
   *  false when the host drives collapse from its own external button —
   *  collapse stays available via `collapsed`/`onCollapsedChange`, just
   *  without the on-separator handle. */
  readonly showHandle?: boolean;
  /** Visual width of the separator line (default 1px); the grab area is wider. */
  readonly separatorWidth?: number;
  readonly className?: string;
  readonly ariaLabel?: string;
}

function readWidth(key: string | undefined, fallback: number, min: number, max: number): number {
  if (!key || typeof localStorage === "undefined") return fallback;
  const v = Number(localStorage.getItem(key));
  return Number.isFinite(v) && v >= min && v <= max ? v : fallback;
}
function readBool(key: string | undefined, fallback: boolean): boolean {
  if (!key || typeof localStorage === "undefined") return fallback;
  const v = localStorage.getItem(key);
  return v === null ? fallback : v === "1";
}

export function ResizablePanel(props: ResizablePanelProps): ReactElement {
  const {
    children,
    side = "left",
    widthKey,
    defaultWidth = 240,
    minWidth = 180,
    maxWidth = 480,
    maxViewportFraction = 0.42,
    collapseKey,
    defaultCollapsed = false,
    collapsible = true,
    showHandle = true,
    separatorWidth = 1,
    className,
    ariaLabel = "Panel",
  } = props;
  const label = ariaLabel.toLowerCase();
  const isLeft = side === "left";

  // Collapse: controlled when `collapsed` is provided, else internal.
  const controlled = props.collapsed !== undefined;
  const [internalCollapsed, setInternalCollapsed] = useState(() =>
    readBool(collapseKey, defaultCollapsed),
  );
  const collapsed = controlled ? !!props.collapsed : internalCollapsed;
  const setCollapsed = useCallback(
    (next: boolean): void => {
      if (!controlled) {
        setInternalCollapsed(next);
        if (collapseKey && typeof localStorage !== "undefined") {
          try {
            localStorage.setItem(collapseKey, next ? "1" : "0");
          } catch {
            /* ignore */
          }
        }
      }
      props.onCollapsedChange?.(next);
    },
    [controlled, collapseKey, props],
  );

  const [width, setWidth] = useState(() => readWidth(widthKey, defaultWidth, minWidth, maxWidth));
  useEffect(() => {
    if (!widthKey || typeof localStorage === "undefined") return;
    try {
      localStorage.setItem(widthKey, String(width));
    } catch {
      /* quota / private mode — width is best-effort */
    }
  }, [widthKey, width]);

  // Track the viewport so the render cap (and the drag clamp) follow window
  // resizes. The cap is computed here in JS rather than as a CSS `min(…, vw)` so
  // a drag begins from the width that's actually on screen — when the stored
  // width exceeds the cap, CSS-only clamping left a dead zone where dragging did
  // nothing until the slack was used up (the "won't resize" bug).
  const [viewportW, setViewportW] = useState(() =>
    typeof window === "undefined" ? Number.POSITIVE_INFINITY : window.innerWidth,
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = (): void => setViewportW(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  // The largest the panel may RENDER: its own max, capped to a viewport
  // fraction. `effectiveMax` never drops below `minWidth` unless the viewport
  // itself is narrower than the minimum.
  const capPx = maxViewportFraction > 0 ? viewportW * maxViewportFraction : Number.POSITIVE_INFINITY;
  const effectiveMax = Math.min(maxWidth, capPx);
  const renderWidth = Math.min(width, effectiveMax);

  // Hold the live render-width + cap in refs so an in-flight drag (whose
  // pointermove closure is fixed at pointerdown) reads the CURRENT values — e.g.
  // if the viewport changes mid-drag — and so the down handler stays stable
  // instead of re-creating on every width/viewport change.
  const renderWidthRef = useRef(renderWidth);
  renderWidthRef.current = renderWidth;
  const effectiveMaxRef = useRef(effectiveMax);
  effectiveMaxRef.current = effectiveMax;

  const onResizeDown = useCallback(
    (e: ReactPointerEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      // Start from the RENDERED width, not the (possibly larger) stored width,
      // so the panel tracks the pointer from pixel one.
      const startW = renderWidthRef.current;
      // Left panel: dragging the handle right widens it; right panel: left.
      const dir = isLeft ? 1 : -1;
      // Drag past the minimum, toward the outer edge, collapses the panel.
      const collapseAt = minWidth - 56;
      const onMove = (ev: PointerEvent): void => {
        const raw = startW + dir * (ev.clientX - startX);
        if (collapsible && raw < collapseAt) {
          setCollapsed(true);
          onUp();
          return;
        }
        setWidth(Math.min(effectiveMaxRef.current, Math.max(minWidth, raw)));
      };
      const onUp = (): void => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [minWidth, isLeft, collapsible, setCollapsed],
  );

  // A slim chevron "handle" on the separator line. Expanded: tucked INTO the
  // panel (collapse). Collapsed: sticking OUT into the main area (expand).
  // Left panel collapses left (◀) / opens right (▶); right panel mirrors.
  const handle = (toCollapse: boolean): ReactElement => {
    const Icon = toCollapse === isLeft ? ChevronLeft : ChevronRight;
    // `toCollapse === isLeft`: collapse on a left panel → ◀; expand on a left
    // panel → ▶ (toCollapse=false, isLeft=true → false===true → ChevronRight).
    return (
      <button
        type="button"
        onClick={() => setCollapsed(toCollapse)}
        title={toCollapse ? `Hide ${label}` : `Show ${label}`}
        aria-label={toCollapse ? `Hide ${label}` : `Show ${label}`}
        aria-expanded={!toCollapse}
        className={cx(
          "absolute top-3 z-hud flex h-12 w-4 items-center justify-center bg-raised/90 text-slate-400 shadow-sm transition-colors hover:bg-hover hover:text-slate-200",
          // Collapse handle (expanded): centered ON the separator line. Expand
          // handle (collapsed): stuck OUT into the main area at the edge.
          toCollapse
            ? "left-1/2 -translate-x-1/2 rounded"
            : isLeft
              ? "left-0 rounded-r" //  flush to the left edge, body into main area
              : "right-0 rounded-l", // flush to the right edge, body into main area
        )}
      >
        <Icon className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />
      </button>
    );
  };

  const wantHandle = collapsible && showHandle;

  // Collapsed: a 0-width rail carrying the expand handle, sticking out. Without
  // the built-in handle the host owns the expand affordance (an external
  // button), so the rail renders nothing.
  if (collapsed) {
    return (
      <div className="relative h-full w-0 shrink-0 overflow-visible">{wantHandle && handle(false)}</div>
    );
  }

  const panel = (
    <div
      // `renderWidth` is the stored width clamped to `effectiveMax` (own max ∩
      // viewport fraction), computed in JS so the drag and the render agree.
      style={{ width: renderWidth }}
      className={cx("relative flex h-full min-h-0 shrink-0 flex-col", className)}
      aria-label={ariaLabel}
    >
      {children}
    </div>
  );
  const separator = (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={`Resize ${label}`}
      title="Drag to resize"
      onPointerDown={onResizeDown}
      style={{ width: separatorWidth }}
      className="relative h-full shrink-0 cursor-col-resize overflow-visible bg-separator transition-colors hover:bg-arch-500/60"
    >
      {/* Wider invisible grab area around the 1px line — raised above both
          neighbours so the whole strip is grabbable, not just the hairline. */}
      <span className="absolute inset-y-0 -left-2 -right-2 z-10" />
      {/* Collapse handle rides the separator, centered on the line. */}
      {wantHandle && handle(true)}
    </div>
  );

  return isLeft ? (
    <>
      {panel}
      {separator}
    </>
  ) : (
    <>
      {separator}
      {panel}
    </>
  );
}
